import { Injectable } from '@angular/core';
import { Observable, Subject, ReplaySubject, timer, onErrorResumeNext, of, never } from 'rxjs';
import { filter, switchMap, catchError, map } from 'rxjs/operators';
import { BaseRestService } from './base-rest.service';
import * as moment from 'moment';

declare var isMobile: any;

@Injectable()
export abstract class BasePollingRestService<T> extends BaseRestService<T> {
    private static readonly MOBILE_POLLING_INTERVAL = 15000;
    private pollingTimerStream: Observable<T>;
    private pollingDataPausable: Observable<T>;
    private pollingDataSubject$: ReplaySubject<T>;
    private pollingDataPauser$: Subject<any>;
    private pollingDataPaused = false;
    private callTime = moment.utc();
    protected throwExecptionOnError = false;

    protected init() {
        // Override and don't register the service here.
    }

    getPollingDataSubject(): ReplaySubject<T> {
        return this.pollingDataSubject$;
    }

    buildPollingRequestParams() {
        return {};
    }

    createPollingDataSubject(interval: number) {
        if (isMobile.any) {
            interval = Math.max(BasePollingRestService.MOBILE_POLLING_INTERVAL, interval);
        }
        this.pollingTimerStream = timer(0, interval)
            .pipe(onErrorResumeNext)
            .pipe(filter((value, index) => {
                const now = Date.now();
                return this.duringRequest === false;
            }))
            .pipe(switchMap(() => {
                if (!this.awsUtil.jwtService) {
                    return;
                }
                if (this.useDemoDataFile() && this.dataCache) {
                    const obs = new Observable((observer) => {
                        observer.next(this.dataCache);
                    });
                    return obs;
                }
                this.duringRequest = true;
                this.callTime = moment.utc();
                return this.observableWithAccessToken(this.buildPollingRequestParams())
                    .pipe(catchError((err, caught) => {
                        this.duringRequest = false;
                        this.serviceRegistry.pollingProblemOccurred();
                        // return Observable.throw(this.handleServiceCallError(err));
                        if (this.throwExecptionOnError) {
                            throw err;
                        } else {
                            return of({} as T);
                        }
                    }));
            }))
            .pipe(map((data: T) => {
                return data;
            }))
            ;
        this.pollingDataPauser$ = new Subject();
        this.pollingDataSubject$ = new ReplaySubject(1);
        // Register it here
        this.registerService();
    }

    registerService() {
        this.serviceRegistry.registerService(this);
    }

    startPolling() {
        if (!this.pollingDataPausable) {
            this.pollingDataPausable = this.pollingDataPauser$.pipe(switchMap(on => (on) ? this.pollingTimerStream : never()));
            this.pollingDataPausable.pipe(onErrorResumeNext).subscribe(this.pollingDataSubject$);
        }
        this.pollingDataPauser$.next(true);
    }

    pause() {
        this.pollingDataPauser$.next(false);
        this.pollingDataPaused = true;
        // this.serviceRegistry.pollingStatus.next(PollingStatus.PAUSED);
    }

    resume() {
        this.pollingDataPauser$.next(true);
        this.pollingDataPaused = false;
        this.duringRequest = false;
        // this.serviceRegistry.pollingStatus.next(PollingStatus.OK);
    }

    isPollingDataPaused() {
        return this.pollingDataPaused;
    }

    lastPolledTime(): moment.Moment {
        return this.callTime;
    }

    protected handleServiceCallError(err: any): any {
        console.log('Error during REST call:' + JSON.stringify(err, null, 2), err);
        this.serviceRegistry.pollingProblemOccurred();
        // Return a valid empty array so streaming doesn't stop
        return [];
    }
}
